// src/components/ShippingPolicy.jsx

import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import BottomNav from '@/components/layouts/Footer/BottomNav';

export default function ShippingPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <main className="container mx-auto px-4 pt-4 md:pb-6 pb-20 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl flex items-center gap-3 justify-center sm:text-4xl font-extrabold text-center mb-8 tracking-tight text-gray-900 dark:text-gray-100">
            <ArrowLeft onClick={() => navigate(-1)} className="cursor-pointer size-8" />
            Shipping Policy
          </h1>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-4 text-base">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
              Our Shipping Process
            </h2>
            <p>
              Thank you for shopping on BigLocal.in! As a marketplace platform, we connect you with
              local sellers (&quot;Vendors&quot;) who list their products on our site. This policy
              outlines how shipping is handled for your orders.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Shipping Responsibility</h3>
            <p>
              All products sold on BigLocal.in are shipped directly by the <strong>Vendors</strong>{' '}
              or their designated <strong>third-party logistics (3PL) partners</strong>. BigLocal.in
              does not directly handle warehousing, packaging, or shipping. The Vendor you purchase
              from is solely responsible for fulfilling your order in a timely and professional
              manner.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">
              Order Processing & Dispatch
            </h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Processing Time:</strong> Vendors typically process and dispatch orders
                within <strong>24-48 hours</strong> on business days. This timeframe is an estimate
                and can vary based on the Vendor&apos;s operational hours and product availability.
              </li>
              <li>
                <strong>Dispatch Notification:</strong> You will receive an email and/or SMS
                notification once your order has been dispatched by the Vendor. This notification
                will include tracking information, if available.
              </li>
            </ul>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">
              Delivery Timelines & Costs
            </h3>
            <h4 className="font-semibold">Estimated Delivery Time</h4>
            <p>
              Delivery times are estimates and depend on the Vendor&apos;s location, your delivery
              address, and the efficiency of the logistics partner. Typical estimates are:
            </p>
            <p>
              <em>
                Please note that delays may occur due to unforeseen circumstances such as public
                holidays, extreme weather conditions, or logistical disruptions.
              </em>
            </p>

            <h4 className="font-semibold">Shipping Costs</h4>
            <p>
              Shipping and handling charges are determined by each Vendor and will be clearly
              displayed during the checkout process before you confirm your payment. Fees may vary
              based on order size, weight, and delivery distance. Some Vendors may offer free
              shipping based on a minimum order value.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Order Tracking</h3>
            <p>
              Once your order is shipped, tracking details will be provided by the Vendor and made
              available in the &quot;My Orders&quot; section of your BigLocal.in account. Please
              allow up to 24 hours for the tracking information to become active after you receive
              the dispatch notification.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">
              Multiple Vendors & Shipments
            </h3>
            <p>
              If your cart contains products from multiple Vendors, your items will be shipped in{' '}
              <strong>separate packages</strong> by each respective Vendor. You will receive
              separate dispatch notifications and tracking numbers for each part of your order.
            </p>

            <div className="my-4 rounded-md border-l-4 border-red-500 bg-red-50 p-4 dark:bg-red-900/20">
              <h5 className="font-bold text-red-800 dark:text-red-300">
                Our Role and Limitation of Liability
              </h5>
              <p className="text-red-700 dark:text-red-200">
                BigLocal.in acts as a facilitator to connect buyers and sellers. We are{' '}
                <strong>not liable</strong> for any delays in shipping, damage that occurs during
                transit, or non-delivery of items. For any shipping-related issues, your primary
                point of contact is the <strong>Vendor</strong>. However, our customer support team
                is available to help mediate and facilitate communication if you face difficulties.
              </p>
            </div>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Delivery Issues</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Incorrect Address:</strong> The customer is responsible for providing a
                complete and accurate delivery address. BigLocal.in and its Vendors are not
                responsible for delivery failures due to an incorrect or incomplete address.
              </li>
              <li>
                <strong>Undeliverable Packages:</strong> If a package is returned to the Vendor as
                undeliverable, the order may be canceled. A refund may be issued, potentially
                deducting the shipping charges incurred by the Vendor.
              </li>
            </ul>
          </div>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
